import { useState, type ReactNode } from 'react';
import { Check, Folder, Plus, Tag } from 'lucide-react';
import {
  AGENT_GROUP_NAME_MAX_LENGTH,
  AGENT_LABEL_NAME_MAX_LENGTH,
  MAX_LAUNCH_LABELS,
} from '@reflex/shared';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { ConfigChip } from '@reflex/ui/components/agent/ConfigChip';
import { cn } from '@reflex/ui/lib/utils';
import { useAgentGroups, useCreateAgentGroup } from '@/hooks/agents/useAgentGroups';
import { useAgentLabels, useCreateAgentLabel } from '@/hooks/agents/useAgentLabels';

/**
 * The launch-time half of the two taxonomy features. Where `AddLabelDialog`
 * and `AddToGroupDialog` file runs that already exist, these chips say where a
 * run belongs before it is launched — which is the only moment the answer is
 * reliably known, since a run you start and walk away from is exactly the one
 * nobody comes back to file.
 *
 * Both chips edit ids, and both can extend the vocabulary in place: a name
 * that isn't in the catalog is created through the normal endpoint (with no
 * members) and the new id is selected. That keeps minting a label in one place
 * rather than teaching the launch payload to accept names as well as ids.
 *
 * The list behaves like {@link TaxonomyPickerDialog} on purpose — same manual
 * filter, same "type a name to create it" affordance — because it is the same
 * decision, made from a chip instead of a dialog.
 */

interface PickerItem {
  id: string;
  name: string;
}

/** Case-insensitive substring filter, matching the dialog's picker. */
function matching<T extends PickerItem>(items: T[], search: string): T[] {
  const needle = search.toLowerCase();
  return items.filter((item) => item.name.toLowerCase().includes(needle));
}

/** Whether `name` already names one of `items`, ignoring case. */
function alreadyNamed(items: PickerItem[], name: string): boolean {
  return items.some((item) => item.name.toLowerCase() === name.toLowerCase());
}

function PickerRow({
  value,
  name,
  detail,
  selected,
  disabled,
  onSelect,
  icon,
  testId,
}: {
  value: string;
  name: string;
  detail?: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  icon?: ReactNode;
  testId: string;
}) {
  return (
    <CommandItem
      value={value}
      disabled={disabled}
      onSelect={() => !disabled && onSelect()}
      data-testid={testId}
      className={cn('gap-2', disabled && 'cursor-not-allowed opacity-50')}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {detail ? <span className="shrink-0 text-2xs text-muted-foreground">{detail}</span> : null}
      {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
    </CommandItem>
  );
}

/** "Create <name>" row, shown when the search text names nothing in the catalog. */
function CreateRow({
  name,
  pending,
  onSelect,
  testId,
}: {
  name: string;
  pending: boolean;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <CommandGroup>
      <CommandItem
        value={`create:${name}`}
        disabled={pending}
        onSelect={() => !pending && onSelect()}
        data-testid={testId}
      >
        <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">Create &ldquo;{name}&rdquo;</span>
      </CommandItem>
    </CommandGroup>
  );
}

export interface AgentLabelsChipProps {
  /** Selected label ids (`alb_*`). */
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Multi-select chip over the organization's label catalog. The popover stays
 * open while picking — labels come in small sets, and reopening the chip
 * between each one is the kind of friction that ends with runs carrying one
 * label instead of the two that describe them.
 */
export function AgentLabelsChip({ value, onChange }: AgentLabelsChipProps) {
  const { data: labels = [] } = useAgentLabels();
  const createLabel = useCreateAgentLabel();
  const [search, setSearch] = useState('');
  const trimmed = search.trim();

  const atCap = value.length >= MAX_LAUNCH_LABELS;
  const selectedNames = value
    .map((id) => labels.find((label) => label.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  // Fall back to a count while the catalog is still loading, so the chip never
  // reads as "Add" with labels actually selected.
  const chipLabel =
    value.length === 0
      ? 'Add'
      : selectedNames.length === 0
        ? `${value.length} labels`
        : value.length > 1
          ? `${selectedNames[0]} +${value.length - 1}`
          : selectedNames[0]!;

  const visible = matching(labels, trimmed);
  const canCreate =
    trimmed.length > 0 &&
    trimmed.length <= AGENT_LABEL_NAME_MAX_LENGTH &&
    !alreadyNamed(labels, trimmed) &&
    !atCap;

  const toggle = (labelId: string) =>
    onChange(value.includes(labelId) ? value.filter((id) => id !== labelId) : [...value, labelId]);

  return (
    <ConfigChip
      ariaLabel="Labels"
      dashed={value.length === 0}
      label={chipLabel}
      icon={
        value.length === 0 ? (
          <Plus className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )
      }
      data-testid="launch-labels-chip"
    >
      {() => (
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search or name a new label…"
            aria-label="Search or name a new label"
          />
          <CommandList className="max-h-64">
            {visible.length === 0 && !canCreate ? (
              <CommandEmpty>Type a name to create your first label.</CommandEmpty>
            ) : null}
            <CommandGroup>
              {visible.map((label) => {
                const selected = value.includes(label.id);
                return (
                  <PickerRow
                    key={label.id}
                    value={label.id}
                    name={label.name}
                    selected={selected}
                    // At the cap the picked labels stay clickable, so a wrong
                    // one can be swapped without clearing the whole set.
                    disabled={atCap && !selected}
                    onSelect={() => toggle(label.id)}
                    icon={<Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    testId={`launch-labels-option-${label.id}`}
                  />
                );
              })}
            </CommandGroup>
            {canCreate ? (
              <CreateRow
                name={trimmed}
                pending={createLabel.isPending}
                testId="launch-labels-create"
                onSelect={() =>
                  createLabel.mutate(
                    { name: trimmed },
                    {
                      onSuccess: (label) => {
                        onChange([...value, label.id]);
                        setSearch('');
                      },
                    },
                  )
                }
              />
            ) : null}
          </CommandList>
          {atCap ? (
            <p className="border-t border-border px-3 py-2 text-2xs text-muted-foreground">
              Up to {MAX_LAUNCH_LABELS} labels per run.
            </p>
          ) : null}
        </Command>
      )}
    </ConfigChip>
  );
}

export interface AgentGroupChipPickerProps {
  /** Selected group id (`agp_*`), or `null` for no group. */
  value: string | null;
  onChange: (next: string | null) => void;
}

/**
 * Single-select chip over the organization's groups. Singular because that is
 * how a run is filed: it belongs to the piece of work it was launched for.
 * Further memberships are added from the agent list afterwards.
 */
export function AgentGroupChipPicker({ value, onChange }: AgentGroupChipPickerProps) {
  const { data: groups = [] } = useAgentGroups();
  const createGroup = useCreateAgentGroup();
  const [search, setSearch] = useState('');
  const trimmed = search.trim();

  const selected = value ? (groups.find((group) => group.id === value) ?? null) : null;
  const chipLabel = !value ? 'Add' : (selected?.name ?? '1 group');
  const visible = matching(groups, trimmed);
  const canCreate =
    trimmed.length > 0 &&
    trimmed.length <= AGENT_GROUP_NAME_MAX_LENGTH &&
    !alreadyNamed(groups, trimmed);

  return (
    <ConfigChip
      ariaLabel="Group"
      dashed={!value}
      label={chipLabel}
      icon={
        value ? (
          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Plus className="h-3.5 w-3.5 shrink-0" />
        )
      }
      data-testid="launch-group-chip"
    >
      {(close) => (
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search or name a new group…"
            aria-label="Search or name a new group"
          />
          <CommandList className="max-h-64">
            {visible.length === 0 && !canCreate ? (
              <CommandEmpty>Type a name to create your first group.</CommandEmpty>
            ) : null}
            <CommandGroup>
              <PickerRow
                value="__none__"
                name="No group"
                selected={!value}
                onSelect={() => {
                  onChange(null);
                  close();
                }}
                testId="launch-group-option-none"
              />
              {visible.map((group) => (
                <PickerRow
                  key={group.id}
                  value={group.id}
                  name={group.name}
                  detail={group.memberCount === 1 ? '1 agent' : `${group.memberCount} agents`}
                  selected={group.id === value}
                  onSelect={() => {
                    onChange(group.id);
                    close();
                  }}
                  icon={<Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                  testId={`launch-group-option-${group.id}`}
                />
              ))}
            </CommandGroup>
            {canCreate ? (
              <CreateRow
                name={trimmed}
                pending={createGroup.isPending}
                testId="launch-group-create"
                onSelect={() =>
                  createGroup.mutate(
                    { name: trimmed },
                    {
                      onSuccess: (group) => {
                        onChange(group.id);
                        setSearch('');
                        close();
                      },
                    },
                  )
                }
              />
            ) : null}
          </CommandList>
        </Command>
      )}
    </ConfigChip>
  );
}
